import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Logger } from "../../infra/logger/logger";
import { AbscenceType } from "domain/entities/abscence-type";
import { AbscenceTypeInputFilter } from "domain/models/abscence-type-input-filter";
import abscenceTypeRepository from "infra/repositories/abscence-type-repository";

export const useAbscenceTypesByFilter = (filter: AbscenceTypeInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["abscenceTypes", filter],
    queryFn: async () => {
      try {
        const result = await abscenceTypeRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_DEFAULT) || 0,
  });
};

export const useAbscenceTypesById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["abscenceType", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("AbscenceType ID inválido");
        const result = await abscenceTypeRepository.getById(id);

        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreateAbscenceTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AbscenceType) => {
      const result = await abscenceTypeRepository.create(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["abscenceTypes", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);

          // Cria um objeto de erro com o array de erros como propriedade
          const customError = new Error("Erro na criação da abscenceType");
          (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
          throw customError;
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
          throw new Error("Erro desconhecido da API");
        }
      } else {
        logger.error("Erro desconhecido", error);
        throw error;
      }
    },
  });
};

export const useUpdateAbscenceTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AbscenceType) => {
      const result = await abscenceTypeRepository.update(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["abscenceTypes", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};

export const useDeleteAbscenceTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AbscenceType) => {
      if (!data || !data.id) throw new Error("AbscenceType ID inválido");
      const result = await abscenceTypeRepository.delete(data.id);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["abscenceTypes", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata.errors);
      }
      throw error;
    },
  });
};
